import { i18next, type Locale } from '@multaqa/i18n'

export const ACCOUNTING_NAMESPACES = [
  'accounting',
  'accounting-assets',
  'accounting-budgets',
  'accounting-reconciliation',
  'accounting-reports',
  'accounting-opening-balances',
  'accounting-working-capital',
] as const

type AccountingNamespace = (typeof ACCOUNTING_NAMESPACES)[number]
type TranslationBundle = Record<string, unknown>
type TranslationModule = TranslationBundle | { default: TranslationBundle }
type ResourceLoader = () => Promise<TranslationModule>

const RESOURCE_LOADERS: Record<Locale, Record<AccountingNamespace, ResourceLoader>> = {
  en: {
    accounting: () => import('@multaqa/i18n/locales/en/accounting.json'),
    'accounting-assets': () => import('@multaqa/i18n/locales/en/accounting-assets.json'),
    'accounting-budgets': () => import('@multaqa/i18n/locales/en/accounting-budgets.json'),
    'accounting-reconciliation': () =>
      import('@multaqa/i18n/locales/en/accounting-reconciliation.json'),
    'accounting-reports': () => import('@multaqa/i18n/locales/en/accounting-reports.json'),
    'accounting-opening-balances': () =>
      import('@multaqa/i18n/locales/en/accounting-opening-balances.json'),
    'accounting-working-capital': () =>
      import('@multaqa/i18n/locales/en/accounting-working-capital.json'),
  },
  ar: {
    accounting: () => import('@multaqa/i18n/locales/ar/accounting.json'),
    'accounting-assets': () => import('@multaqa/i18n/locales/ar/accounting-assets.json'),
    'accounting-budgets': () => import('@multaqa/i18n/locales/ar/accounting-budgets.json'),
    'accounting-reconciliation': () =>
      import('@multaqa/i18n/locales/ar/accounting-reconciliation.json'),
    'accounting-reports': () => import('@multaqa/i18n/locales/ar/accounting-reports.json'),
    'accounting-opening-balances': () =>
      import('@multaqa/i18n/locales/ar/accounting-opening-balances.json'),
    'accounting-working-capital': () =>
      import('@multaqa/i18n/locales/ar/accounting-working-capital.json'),
  },
}

function normalizeModule(module: TranslationModule): TranslationBundle {
  if ('default' in module && typeof module.default === 'object' && module.default !== null) {
    return module.default as TranslationBundle
  }

  return module as TranslationBundle
}

async function loadResource(locale: Locale, namespace: AccountingNamespace): Promise<void> {
  const resource = normalizeModule(await RESOURCE_LOADERS[locale][namespace]())

  i18next.addResourceBundle(locale, namespace, resource, true, true)
}

let accountingNamespacesPromise: Promise<void> | undefined
let accountingNamespacesLoaded = false

function hasAccountingResources(): boolean {
  return (['en', 'ar'] as const).every((locale) =>
    ACCOUNTING_NAMESPACES.every((namespace) => i18next.hasResourceBundle(locale, namespace)),
  )
}

export function loadAccountingNamespaces(): Promise<void> {
  if (accountingNamespacesPromise && (!accountingNamespacesLoaded || hasAccountingResources())) {
    return accountingNamespacesPromise
  }

  accountingNamespacesPromise = undefined
  accountingNamespacesLoaded = false

  accountingNamespacesPromise = Promise.all(
    (Object.keys(RESOURCE_LOADERS) as Locale[]).flatMap((locale) =>
      ACCOUNTING_NAMESPACES.map((namespace) => loadResource(locale, namespace)),
    ),
  )
    .then(() => {
      accountingNamespacesLoaded = true
    })
    .catch((error: unknown) => {
      accountingNamespacesPromise = undefined
      accountingNamespacesLoaded = false
      throw error
    })

  return accountingNamespacesPromise
}
